import { useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router-dom';

import styled from 'styled-components';

import { AvatarTag } from '@components/avatar/avatar-tag';
import { Badge } from '@components/badge';
import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { Header } from '@components/header';
import { COLOR_TO_VARIANT, ICON_CHOOSER_ICONS } from '@components/icon-chooser/icon-chooser';
import { IconFancyContainer } from '@components/icon-fancy-container';
import { IconDocument } from '@components/icons/document';
import { IconForm } from '@components/icons/form';
import { IconWorkspace } from '@components/icons/workspace';
import { ListCard } from '@components/list-card';
import { PageContent } from '@components/page-containers/page-container';
import { Pagination, usePagination } from '@components/pagination';
import { Separator } from '@components/separator';
import { Stack } from '@components/stack';
import { ValueList } from '@components/value-list';
import { useScopedTranslate } from '@locale/translate';
import { storage } from '@utils/browser';
import { pxToRem } from '@utils/converters';
import { toQueryString } from '@utils/requests';

import { useDocumentTemplates } from 'api/documents/hooks';
import { DocumentTemplate } from 'api/documents/requests';
import { useWorkspace } from 'api/workspaces/hooks';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { PageNotFound } from 'ui/authenticated/main/pages/page-not-found';
import { routePaths } from 'ui/authenticated/navigation';

import { useDocumentAddEditSidebar } from './add-edit-sidebar';
import { DocumentActions } from './common';

const FORMS_PER_PAGE = 25;

const pageNumberStoreId = 'document-template-page-number';

export const DocumentsList = () => {
  const { t } = useScopedTranslate(locale);
  const [searchStr, setSearchStr] = useState('');
  const {
    workspaceId,
    title: workspaceTitle,
    color: workspaceColor,
    icon: workspaceIcon,
  } = useCurrentWorkspace();
  const { isError: isWorkspaceError, isLoading: isWorkspaceLoading } = useWorkspace(workspaceId);
  const showSidebar = useDocumentAddEditSidebar();
  const queryString = useMemo(() => {
    return toQueryString({
      search: searchStr,
      workspace: workspaceId,
    });
  }, [searchStr, workspaceId]);
  const WorkspaceIcon = ICON_CHOOSER_ICONS[workspaceIcon] || IconWorkspace;

  const templatesQuery = useDocumentTemplates(queryString, isWorkspaceError || isWorkspaceLoading);

  const { numPages, selectedPage, selectPage, getItemsForPage } = usePagination({
    pageSize: FORMS_PER_PAGE,
    defaultPage: Number(storage.get(pageNumberStoreId)) || 1,
    count: (templatesQuery.data ?? []).length,
  });

  useEffect(() => {
    storage.set(pageNumberStoreId, String(selectedPage));
  }, [selectedPage]);

  useEffect(() => {
    if (selectedPage !== 1) {
      selectPage(1);
    }
  }, [templatesQuery.data]); // oxlint-disable-line react-hooks/exhaustive-deps
  if (isWorkspaceError) {
    return <PageNotFound message={t('notFoundMessage')} />;
  }

  const Content = () => {
    if (!templatesQuery.data) {
      return null;
    }
    const templates = getItemsForPage(templatesQuery.data) as DocumentTemplate[];

    return (
      <PageContent>
        {templates.length === 0 &&
          (searchStr ? (
            <EmptySearchState skipMargin />
          ) : (
            <EmptyState
              skipMargin
              header={t('emptyHeader')}
              content={t('emptyContent')}
              infoLink={{
                label: t('emptyLinkLabel'),
                onClick: () => showSidebar({}, { isOverrideEnabled: true }),
              }}
              icon={<IconForm size={80} />}
            />
          ))}
        <Container>
          {templates.map((template) => (
            <TemplateRow key={template.shortId} document={template} />
          ))}
        </Container>
        <PaginationContainer>
          <Pagination
            justify="right"
            count={numPages}
            selected={selectedPage}
            onSelect={selectPage}
          />
        </PaginationContainer>
      </PageContent>
    );
  };

  return (
    <>
      <Header
        breadcrumbs={<Breadcrumb items={[{ label: workspaceTitle }, { label: t('title') }]} />}
        actions={
          <Stack space={2} orientation="horizontal">
            <ExpandableSearch onChange={setSearchStr} />

            <Button
              variant="primary"
              iconLeft={<IconDocument />}
              onClick={() => showSidebar()}
              text={t('createDocument')}
            />
          </Stack>
        }
        backAction={
          <IconContainer
            hasBorder
            icon={<WorkspaceIcon color={workspaceColor} />}
            variant={COLOR_TO_VARIANT[workspaceColor]}
          />
        }
      />

      <Content />
    </>
  );
};

const TemplateRow = ({ document }: { document: DocumentTemplate }) => {
  const { t } = useScopedTranslate(locale);
  const { workspaceId } = useCurrentWorkspace();

  return (
    <ListCard
      spaceX={6}
      spaceY={4}
      to={generatePath(routePaths.worksspaceDocumentDetail, {
        workspaceId,
        documentId: document.shortId,
        '*': '',
      })}
      title={document.name}
      description={document.description ?? ''}
      icon={<IconDocument />}
      actions={
        <Stack space={4} orientation="horizontal" align="center">
          <StatusTag status={document.status} data-testid="status" />
          <Separator orientation="horizontal" />
          <AvatarTag
            name={document.createdByUser?.name ?? ''}
            src={document.createdByUser?.picture}
            style={{ opacity: document.createdByUser ? 1 : 0 }}
          />
          <Separator orientation="horizontal" />
          <ValueList
            items={[
              { value: document.numJourneys, label: t('journeys') },
              { value: document.fieldConfig.length, label: t('variables') },
            ]}
          />
          <DocumentActions document={document} hasDelete />
        </Stack>
      }
    />
  );
};

const StatusTag = ({ status, ...rest }: Pick<DocumentTemplate, 'status'>) => {
  const { t } = useScopedTranslate(locale);

  if (status === 'active') {
    return <Badge size="md" variant="green" {...rest} text={t('active')} />;
  }

  return <Badge size="md" variant="neutral" {...rest} text={t('disabled')} />;
};

const locale = {
  en: {
    title: 'Documents',
    emptyHeader: 'No document templates',
    emptyContent: 'Upload the documents you wish to use as templates in multiple journeys',
    emptyLinkLabel: 'Learn more about document templates',
    createDocument: 'Add document',
    journeys: 'Journeys',
    variables: 'Variables',
    notFoundMessage: 'Are you sure the workspace exists and you have access to it?',
    active: 'Active',
    disabled: 'Disabled',
  },
};

const Container = styled(Stack).attrs({ space: 2 })({
  '.separator': {
    height: pxToRem(32),
  },
});

const PaginationContainer = styled.div({
  paddingTop: pxToRem(24),
  paddingBottom: pxToRem(24),
});

const IconContainer = styled(IconFancyContainer)({
  borderWidth: pxToRem(1),
});
